"use client"

import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { collection, query, where, getDocs } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { Loader2, ArrowLeft, Clock, HelpCircle, CheckCircle2, XCircle } from "lucide-react"
import BackButton from "@/components/ui/back-button"

export default function QuizPage() {
  const { slug } = useParams()
  const [quiz, setQuiz] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0)
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({})
  const [isFinished, setIsFinished] = useState(false)
  
  // Timer state
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  useEffect(() => {
    const fetchQuiz = async () => {
       try {
         // Try finding in daily quizzes first
         let q = query(collection(db, "quizzes"), where("slug", "==", slug))
         let snapshot = await getDocs(q)
         
         if (snapshot.empty) {
           // Try finding in test series if not in daily quizzes
           q = query(collection(db, "test_series"), where("slug", "==", slug))
           snapshot = await getDocs(q)
         }

         if (!snapshot.empty) {
           const data = snapshot.docs[0].data()
           setQuiz({ id: snapshot.docs[0].id, ...data })
           if (data.duration) {
             setTimeLeft(data.duration * 60)
           }
         } else {
           setError("Quiz not found.")
         }
       } catch (err) {
         setError("Failed to load quiz.")
       } finally {
         setIsLoading(false)
       }
    }
    if (slug) fetchQuiz()
  }, [slug])

  useEffect(() => {
    if (timeLeft === null || isFinished) return
    if (timeLeft <= 0) {
       setIsFinished(true)
       return
    }
    const timer = setInterval(() => setTimeLeft(prev => prev! - 1), 1000)
    return () => clearInterval(timer)
  }, [timeLeft, isFinished])

  const handleOptionSelect = (optionIndex: number) => {
    if (isFinished) return
    setSelectedOptions(prev => ({ ...prev, [currentQuestionIdx]: optionIndex }))
  }

  const handleNext = () => {
    if (currentQuestionIdx < quiz.questions.length - 1) {
      setCurrentQuestionIdx(prev => prev + 1)
    } else {
      setIsFinished(true)
    }
  }

  const calculateScore = () => {
    let score = 0
    quiz.questions.forEach((q: any, i: number) => {
      if (selectedOptions[i] === q.correctOptionIndex) score++
    })
    return score
  }

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center space-y-4">
        <Loader2 className="w-8 h-8 text-[#b48c36] animate-spin" />
        <p className="text-[#b48c36] font-bold text-[10px] uppercase tracking-[0.4em]">Loading Assessment...</p>
      </div>
    )
  }

  if (error || !quiz) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center">
        <p className="text-xl font-serif text-slate-950 mb-4">{error || "Assessment Unavailable"}</p>
        <Link to="/" className="text-[#b48c36] font-bold text-[10px] uppercase tracking-widest hover:underline">
           Return to Safety
        </Link>
      </div>
    )
  }

  const currentQuestion = quiz.questions[currentQuestionIdx]
  const isLastQuestion = currentQuestionIdx === quiz.questions.length - 1
  const hasSelected = selectedOptions[currentQuestionIdx] !== undefined

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  if (isFinished) {
    const score = calculateScore()
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
         <div className="bg-white border border-slate-200 shadow-xl p-8 sm:p-12 text-center">
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-950 mb-4">Assessment Complete</h1>
            <p className="text-slate-500 mb-8">{quiz.title}</p>
            
            <div className="flex justify-center mb-10">
               <div className="w-32 h-32 rounded-full border-4 border-[#b48c36] flex flex-col items-center justify-center">
                  <span className="text-4xl font-serif font-black text-slate-950">{score}</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#b48c36]">Out of {quiz.questions.length}</span>
               </div>
            </div>

            <div className="space-y-6 text-left border-t border-slate-100 pt-8 mt-8">
               <h3 className="font-bold text-slate-950 uppercase tracking-widest text-xs mb-4">Detailed Review</h3>
               {quiz.questions.map((q: any, i: number) => {
                 const studentAns = selectedOptions[i]
                 const isCorrect = studentAns === q.correctOptionIndex
                 return (
                   <div key={i} className={`p-6 border-l-4 ${isCorrect ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
                      <div className="flex items-start gap-4">
                         {isCorrect ? <CheckCircle2 className="text-emerald-500 shrink-0 mt-1" /> : <XCircle className="text-red-500 shrink-0 mt-1" />}
                         <div>
                            <p className="text-sm font-medium text-slate-900 mb-4">{q.questionText}</p>
                            <p className="text-xs text-slate-600 mb-2">
                               <span className="font-bold">Your Answer:</span> {studentAns !== undefined ? q.options[studentAns] : 'Not Answered'}
                            </p>
                            {!isCorrect && (
                               <p className="text-xs text-slate-600 mb-2">
                                  <span className="font-bold text-emerald-600">Correct Answer:</span> {q.options[q.correctOptionIndex]}
                               </p>
                            )}
                            {q.explanation && (
                               <div className="mt-4 p-4 bg-white/50 border border-slate-200 text-xs text-slate-700">
                                  <span className="font-bold block mb-1">Explanation:</span>
                                  {q.explanation}
                               </div>
                            )}
                         </div>
                      </div>
                   </div>
                 )
               })}
            </div>

            <div className="mt-12 flex justify-center">
               <Link to="/" className="bg-slate-950 text-white px-8 py-4 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors">
                  Return to Dashboard
               </Link>
            </div>
         </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <BackButton className="mb-8" />
        
        <div className="bg-white border border-slate-200 shadow-xl overflow-hidden">
           {/* Header */}
           <div className="bg-slate-950 p-6 sm:p-8 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                 <p className="text-[#b48c36] font-bold text-[10px] uppercase tracking-widest mb-2">Official Assessment</p>
                 <h1 className="text-xl sm:text-2xl font-serif font-medium">{quiz.title}</h1>
              </div>
              <div className="flex items-center gap-6 text-[10px] font-bold uppercase tracking-widest">
                 <div className="flex items-center gap-2">
                    <HelpCircle size={14} className="text-[#b48c36]" />
                    Question {currentQuestionIdx + 1} of {quiz.questions.length}
                 </div>
                 {timeLeft !== null && (
                   <div className={`flex items-center gap-2 ${timeLeft < 60 ? 'text-red-400' : 'text-slate-300'}`}>
                      <Clock size={14} className={timeLeft < 60 ? 'animate-pulse text-red-500' : ''} />
                      {formatTime(timeLeft)}
                   </div>
                 )}
              </div>
           </div>

           {/* Progress Bar */}
           <div className="w-full bg-slate-100 h-1">
              <div 
                className="bg-[#b48c36] h-1 transition-all duration-300"
                style={{ width: `${((currentQuestionIdx) / quiz.questions.length) * 100}%` }}
              />
           </div>

           {/* Question Container */}
           <div className="p-6 sm:p-12">
              <h2 className="text-lg sm:text-xl font-medium text-slate-900 leading-relaxed mb-8">
                {currentQuestion.questionText}
              </h2>

              <div className="space-y-3">
                 {currentQuestion.options.map((option: string, idx: number) => {
                    const isSelected = selectedOptions[currentQuestionIdx] === idx
                    return (
                       <button
                         key={idx}
                         onClick={() => handleOptionSelect(idx)}
                         className={`w-full text-left p-4 sm:p-5 border transition-all duration-200 flex items-start gap-4 ${isSelected ? 'border-[#b48c36] bg-[#b48c36]/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                       >
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${isSelected ? 'border-[#b48c36] bg-[#b48c36]' : 'border-slate-300'}`}>
                             {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                          </div>
                          <span className={`${isSelected ? 'text-slate-950 font-medium' : 'text-slate-600'} text-sm`}>
                            {option}
                          </span>
                       </button>
                    )
                 })}
              </div>

              <div className="mt-10 flex justify-end">
                 <button 
                   onClick={handleNext}
                   disabled={!hasSelected}
                   className="bg-slate-950 text-white px-8 py-4 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors disabled:opacity-50 disabled:hover:bg-slate-950"
                 >
                    {isLastQuestion ? "Submit Assessment" : "Save & Next"}
                 </button>
              </div>
           </div>
        </div>
      </div>
    </div>
  )
}
